import React, { useEffect, useRef } from "react";
import { Warning } from "@phosphor-icons/react";
import { useUser, useAuth } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import SideNav from "../Navs/sidenav";
import useValidatePageAccess from "../../hooks/useValidatePageAccess.js";
import TryAgain from "../home-page/TryAgain.js";
import { useDispatch, useSelector } from "react-redux";
import { setEmail, setMobileSideNav } from "src/store/slices/HomeSlice";
import { trackAnalytics } from "../helpers.js";
import usePageTracking from "src/hooks/usePageTracking.js";
import { ErrorLoggingV2 } from "src/utils/errorHelpers.js";
import { EVENT_NAMES, EVENT_PARAMETERS, getPageName, MODAL_NAMES } from "src/utils/EventConstants.js";
import { getUserMetadata } from "src/store/slices/onboardingSlice";
import InhouseLoader from "src/components/InhouseLoader/InhouseLoader";

const BaseLayout = ({ children }) => {
  const showTryAgainPage = useSelector((state) => state.onboarding.showTryAgainPage);
  const documentPanelOpen = useSelector((state) => state.messageSlice.documentPanelOpen);
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { isLoaded, user } = useUser();
  const { isSignedIn, getToken } = useAuth();
  const [message, setMessage] = React.useState(
    `We're experiencing a temporary issue loading data. Please refresh the app to try again. `,
  );
  const [showSidenav, setShowSidenav] = React.useState(true);
  const tokenRetryAttemptedRef = useRef(false);
  const [isRetryingToken, setIsRetryingToken] = React.useState(false);
  const userMetadataFetchStatus = useSelector((state) => state.onboarding.userMetadataFetchStatus);
  const userMetadata = useSelector((state) => state.onboarding.userMetadata);
  const upgradeProUserToCounselStatus = useSelector((state) => state.chat.upgradeProUserToCounselStatus);
  const mobileSideNav = useSelector((state) => state.homeslice.mobileSideNav);
  const preparingCheckout = useSelector((state) => state.chat.preparingCheckout);

  const handleCloseMobileSidenav = () => {
    if (window.innerWidth < 768 && mobileSideNav) {
      dispatch(setMobileSideNav(false));
    }
  };

  useEffect(() => {
    if (upgradeProUserToCounselStatus === "failed") {
      try {
        trackAnalytics(EVENT_NAMES.SENT_ERROR_MESSAGE, {
          [EVENT_PARAMETERS.PAGE_NAME]: getPageName,
          [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
          [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
          [EVENT_PARAMETERS.MODAL_NAME]: MODAL_NAMES.PRICING_MODAL_PRO,
        });
      } catch (e) {
        console.error("Error tracking SENT_ERROR_MESSAGE event", e);
      }
    }
    if (upgradeProUserToCounselStatus === "success") {
      dispatch(getUserMetadata());
      var url = window.location.pathname;

      const params = new URLSearchParams(window.location.search);
      params.set("action_type", "finalization");
      url += "?" + params.toString();

      navigate(url, { replace: true });
    }
  }, [upgradeProUserToCounselStatus, dispatch, navigate, userMetadata?.id, userMetadata?.subscription_type]);

  // Fetch user metadata only after Clerk provides a valid token
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (userMetadataFetchStatus !== "idle") return;
    const fetchWithFreshToken = async () => {
      try {
        const token = await getToken({ template: "Backend" });
        if (token && typeof token === "string") {
          localStorage.setItem("accessToken2", token);
        }
      } catch (e) {
        // Clerk couldn't provide token — fall through with whatever is in localStorage
      }
      // Always dispatch: backend 401 will trigger the token-failure retry below
      dispatch(getUserMetadata());
    };
    fetchWithFreshToken();
  }, [isLoaded, isSignedIn, getToken, dispatch, userMetadataFetchStatus]);

  useEffect(() => {
    if (user) {
      dispatch(setEmail(user?.primaryEmailAddress?.emailAddress));
      setShowSidenav(true);
      if (userMetadata && userMetadataFetchStatus === "success") {
        if (!userMetadata?.individual_info && !userMetadata?.entity_info) {
          navigate("/onboarding");
        }
      }
    } else {
      if (window.location.href.toLowerCase().search("/shared/") !== -1) {
        setShowSidenav(false);
      }
    }
  }, [user, userMetadata]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userMetadataFetchStatus === "success") {
      setIsRetryingToken(false);
    }
    if (
      userMetadataFetchStatus !== "success" &&
      userMetadataFetchStatus !== "loading" &&
      userMetadataFetchStatus !== "idle"
    ) {
      switch (userMetadataFetchStatus) {
        case "token-failure":
          if (!tokenRetryAttemptedRef.current) {
            // Get a fresh token directly from Clerk and retry once
            tokenRetryAttemptedRef.current = true;
            setIsRetryingToken(true);
            const refreshAndRetry = async () => {
              try {
                const freshToken = await getToken({ template: "Backend" });
                if (freshToken && typeof freshToken === "string") {
                  localStorage.setItem("accessToken2", freshToken);
                  dispatch(getUserMetadata());
                  return;
                }
              } catch (e) {
                // Clerk couldn't provide fresh token
              }
              setIsRetryingToken(false);
              setMessage("We are unable to log you in. Please try again.");
            };
            refreshAndRetry();
          } else {
            setIsRetryingToken(false);
            setMessage("We are unable to log you in. Please try again.");
          }
          break;
        case "profile-not-completed":
          setMessage(
            <>
              Your Profile information isn't complete. Update your profile here <br />
              <a href="/onboarding" className="underline decoration-solid text-black text-center">
                Update Profile
              </a>
            </>,
          );
          break;
        default:
          setMessage(<>We're experiencing a temporary issue loading data. Please refresh the app to try again. </>);
      }
    }
  }, [userMetadataFetchStatus, userMetadata, getToken]);

  usePageTracking();

  useValidatePageAccess();

  // Show loader while Clerk is loading, checking auth status, or retrying token
  if (
    isRetryingToken ||
    !isLoaded ||
    (isLoaded && !isSignedIn && window.location.href.toLowerCase().search("/shared/") === -1)
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <InhouseLoader size={48} />
      </div>
    );
  }

  return (
    <>
      {userMetadataFetchStatus !== "success" &&
        userMetadataFetchStatus !== "loading" &&
        userMetadataFetchStatus !== "idle" &&
        userMetadataFetchStatus !== "anonymous-id-exception" &&
        userMetadataFetchStatus !== "thread-not-found" ? (
        <div className="w-full h-screen flex justify-center items-center flex-col gap-[8px]">
          <div className="md:w-[458px] w-full rounded-[8px] border px-[16px] py-[15px] flex gap-[8px] justify-center items-start bg-[#FFF6F5] ">
            <span className="w-[24px] h-[24px] my-auto">
              <Warning size={24} className="text-red-700" />
            </span>
            <p className="font-sans text-[16px] font-medium leading-[25px]">{message}</p>
          </div>
          <p className="font-sans font-medium text-[14px] leading-[22px] text-[#333333]">
            Reach out to the{" "}
            <a
              href="mailto:support@inhouse.ai"
              target="_blank"
              rel="noreferrer"
              className="underline decoration-solid text-black"
            >
              support team
            </a>{" "}
            if the problem persists
          </p>
          {userMetadataFetchStatus !== "token-failure" ? (
            <p className="mx-auto">
              <a href="/home" className="underline">
                Home
              </a>
            </p>
          ) : (
            <p className="mx-auto">
              <p
                onClick={() => {
                  window.location.reload();
                }}
                className="underline cursor-pointer"
              >
                Refresh and try again
              </p>
            </p>
          )}
        </div>
      ) : (
        <>
          {showTryAgainPage ? (
            <TryAgain />
          ) : (
            <div className="w-full h-screen overflow-x-hidden overflow-y-hidden">
              <div className="flex flex-row w-full h-full relative">
                {showSidenav && <SideNav />}
                {/* Backdrop overlay for mobile sidenav */}
                {mobileSideNav && (
                  <div className="fixed inset-0 bg-black/30 z-[99] md:hidden" onClick={handleCloseMobileSidenav} />
                )}
                {preparingCheckout && window.location.pathname.includes("/chat/") && (
                  <div className="fixed inset-0 flex flex-col items-center justify-center bg-white/65 z-[200]">
                    <InhouseLoader size={48} />
                    <span className="text-[15px] font-[600] text-[#0A0A0A] mt-1">Preparing checkout...</span>
                  </div>
                )}
                <div className="flex-1 min-w-0" onClick={handleCloseMobileSidenav}>
                  {children}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default BaseLayout;
